import React, { FC } from 'react';
import { FormattedDate } from 'src/components/formatted-date/formatted-date';
import styled from 'styled-components';
import { CreditBlockData } from '../../components/credit-block-data/credit-block-data';
import { ShippingConditionData } from '../../components/shipping-condition-data/shipping-condition-data';
import { StatusBadge } from '../../components/status-badge/status-badge';
import { toDisplayDate, toDisplayTime } from '../../constants/dates';
import { IItem, IOrder } from '../../graphql/types';
import { Black2, Grey } from '../../styles/colours';
import { Medium } from '../../styles/fonts';

const DetailsCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0rem 0 3rem 0;
`;

const DataCellTop = styled.div`
  display: flex;
  flex-direction: column;

  padding: 1rem 2rem;

  &:first-child {
    padding-left: 0;
  }
`;

const DataCellLower = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 0.5px solid ${Grey};

  padding: 1rem 4rem;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    border-right: none;
  }
`;

const DataLabel = styled.div`
  color: ${Black2};
`;

const DataValueTop = styled.div`
  font-family: ${Medium};
  font-size: 1.2rem;
  padding-top: 0.5rem;
`;

const DataValueLower = styled.div`
  font-family: ${Medium};
  font-size: 1rem;
  padding-top: 0.5rem;
`;

export const DataRow = styled.div`
  display: inline-flex;
  flex-direction: row;
  flex: 1;
`;

export const StyledRowTop = styled.div`
  display: inline-flex;
  flex-direction: row;
`;

export const StyledRowBottom = styled.div`
  display: inline-flex;
  flex-direction: row;
  padding-top: 2rem;
`;

export const checkIfRetail = (order: IOrder): boolean => {
  return (
    order.orderType === 'ZVMI' ||
    order.orderType === 'ZORE' ||
    (order.orderType === 'ZOR' &&
      order.division === '02' &&
      order.distributionChannel === '14')
  );
};

export const isBackOrdered = (item: IItem): boolean =>
  item.backorderedQuantity > 0 &&
  item.backorderedQuantity <= item.outstandingQuantity;

export const isDelivered = (item: IItem) => item.status === 'INVOICED';

export const OrderSummaryCard: FC<{ order: IOrder }> = ({ order }) => {
  const items = order.items ? order.items : [];
  const numberOfItems = items.length;
  const numberOfBackOrderedItems = items.filter(isBackOrdered).length;
  const numberOfInvoicedItems = items.filter(isDelivered).length;

  return (
    <DetailsCard data-type="order-summary">
      <StyledRowTop>
        <DataCellTop>
          <DataLabel>Shell order number</DataLabel>
          <DataValueTop data-type="order-id">{order.id}</DataValueTop>
        </DataCellTop>
        <DataCellTop>
          <DataLabel>Purchase order no</DataLabel>
          <DataValueTop data-type="order-poid">{order.poID}</DataValueTop>
        </DataCellTop>
        <DataCellTop>
          <DataLabel>Created By</DataLabel>
          <DataValueTop data-type="order-origin">{order.origin}</DataValueTop>
        </DataCellTop>
        <DataCellTop>
          <DataLabel>Shipping condition</DataLabel>
          <DataValueTop data-type="order-shipping-condition">
            <ShippingConditionData order={order} />
          </DataValueTop>
        </DataCellTop>
        <DataCellTop>
          <DataLabel>Order type</DataLabel>
          <DataValueTop data-type="order-type">{order.orderType}</DataValueTop>
        </DataCellTop>
        <DataCellTop>
          <DataLabel>Order status</DataLabel>
          <DataValueTop data-type="order-status">
            <StatusBadge
              title={order.statusLastUpdated}
              status={order.status}
            />
          </DataValueTop>
        </DataCellTop>
      </StyledRowTop>
      <StyledRowBottom>
        {checkIfRetail(order) && order.etaDate.trim().length !== 0 && (
          <DataCellLower>
            <DataLabel>ETA</DataLabel>
            <DataValueLower data-type="eta">
              {toDisplayDate(order.etaDate)}
            </DataValueLower>
            <DataValueLower>{toDisplayTime(order.etaDate)}</DataValueLower>
          </DataCellLower>
        )}
        {order.creditStatus && (
          <DataCellLower>
            <DataLabel>Credit block</DataLabel>
            <DataValueLower data-type="credit-block">
              <CreditBlockData order={order} />
            </DataValueLower>
          </DataCellLower>
        )}
        <DataCellLower>
          <DataLabel>Backordered</DataLabel>
          <DataValueLower data-type="order-backordered-items">
            {numberOfBackOrderedItems}/{numberOfItems}
          </DataValueLower>
        </DataCellLower>
        <DataCellLower>
          <DataLabel>Delivered</DataLabel>
          <DataValueLower data-type="order-invoiced-items">
            {numberOfInvoicedItems}/{numberOfItems}
          </DataValueLower>
        </DataCellLower>
        {order.deliveryBlock && (
          <DataCellLower>
            <DataLabel>Delivery block</DataLabel>
            <DataValueLower data-type="order-delivery-block">
              {order.deliveryBlock}
            </DataValueLower>
          </DataCellLower>
        )}
        <DataCellLower>
          <DataLabel>Order created</DataLabel>
          <DataValueLower data-type="created-on">
            <FormattedDate date={order.createdOn} />
          </DataValueLower>
          <DataValueLower>{order.createdAt}</DataValueLower>
        </DataCellLower>
      </StyledRowBottom>
    </DetailsCard>
  );
};
